import { useState } from 'react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../../ui/chart';
import { safetyChartConfig, SC } from '../../constants';

export interface SentimentBarDatum {
  name: string;
  Positive: number;
  Neutral: number;
  Negative: number;
  Toxic: number;
}

// Figma colours (node 1475:36939) — shared with the rest of BrandSafety via `SC`.
const SENT = {
  Positive: SC.positive,
  Neutral: SC.neutral,
  Negative: SC.negative,
  Toxic: SC.toxic,
};

// Legend order per Figma: Negative · Neutral · Positive · Toxic.
const LEGEND = [
  { label: 'Negative', color: SENT.Negative },
  { label: 'Neutral', color: SENT.Neutral },
  { label: 'Positive', color: SENT.Positive },
  { label: 'Toxic', color: SENT.Toxic },
];

// Figma's own example video titles truncate at ~16 chars ("Claim FREE Certi…"),
// but at this card's real (narrow) width a 16-char label rotated -30° can run
// past the chart's left edge and get clipped by the SVG's own bounds. 11
// chars keeps the readable start fully on-screen even for the leftmost bar.
// Truncating from the end (not the middle/start) keeps the readable start of
// the title visible; the full title is available via the native <title> tooltip.
const MAX_TICK_CHARS = 11;
const truncateEnd = (value: string) =>
  value.length > MAX_TICK_CHARS ? `${value.slice(0, MAX_TICK_CHARS)}…` : value;

function VideoNameTick({ x, y, payload }: { x?: string | number; y?: string | number; payload?: { value?: unknown } }) {
  const full = String(payload?.value ?? '');
  const truncated = full.length > MAX_TICK_CHARS;
  return (
    <g transform={`translate(${x},${y})`}>
      <text transform="rotate(-30)" textAnchor="end" dy={8} fontSize={12} fontWeight={500} fill="#404040">
        {truncateEnd(full)}
        {truncated && <title>{full}</title>}
      </text>
    </g>
  );
}

type SentKey = keyof typeof SENT;
// `index: null` = every bar of this category (legend hover); a number = one
// specific bar's segment only (hovering the segment itself) — mirrors the
// donut's single-segment highlight instead of dimming a whole color everywhere.
interface Hovered { key: SentKey; index: number | null }

/** Figma "Sentiment Distribution per Video" card (node 1475:36939). Hovering
 *  a single bar segment highlights just that segment (dims everything else);
 *  hovering the legend highlights that category across every bar — matches
 *  the Channel Average donut's interactive pattern. */
export function SentimentDistributionChart({ barData }: { barData: SentimentBarDatum[] }) {
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const opacityFor = (key: SentKey, index: number) =>
    hovered == null || (hovered.key === key && (hovered.index == null || hovered.index === index)) ? 1 : 0.25;
  const barProps = (key: SentKey) => ({
    isAnimationActive: false as const,
    dataKey: key,
    stackId: 'a',
    barSize: 44,
  });
  const cellsFor = (key: SentKey) =>
    barData.map((_, i) => (
      <Cell
        key={i}
        fill={SENT[key]}
        fillOpacity={opacityFor(key, i)}
        onMouseEnter={() => setHovered({ key, index: i })}
        onMouseLeave={() => setHovered(null)}
        style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s ease' }}
      />
    ));

  return (
    <div
      style={{
        boxSizing: 'border-box',
        minHeight: 402,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: '#171717' }}>
        SENTIMENT DISTRIBUTION PER VIDEO
      </div>

      <ChartContainer config={safetyChartConfig} className="min-h-0 w-full flex-1">
        <BarChart data={barData} margin={{ left: 24, right: 4, top: 8, bottom: 4 }} barCategoryGap="40%">
          <CartesianGrid vertical={false} strokeDasharray="4 3" stroke="#E5E5E5" />
          <XAxis
            dataKey="name"
            tick={VideoNameTick}
            interval={0}
            height={72}
            tickLine={false}
            axisLine={{ stroke: '#E5E5E5', strokeWidth: 1.5 }}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tick={{ fontSize: 12, fontWeight: 500, fill: '#737373' }}
            tickLine={false}
            axisLine={false}
            width={34}
          />
          <ChartTooltip content={<ChartTooltipContent />} cursor={{ fill: 'rgba(109,40,217,0.04)' }} />
          <Bar {...barProps('Positive')}>{cellsFor('Positive')}</Bar>
          <Bar {...barProps('Neutral')}>{cellsFor('Neutral')}</Bar>
          <Bar {...barProps('Negative')}>{cellsFor('Negative')}</Bar>
          <Bar {...barProps('Toxic')} radius={[4, 4, 0, 0]}>{cellsFor('Toxic')}</Bar>
        </BarChart>
      </ChartContainer>

      {/* Custom legend — Negative · Neutral · Positive · Toxic */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 18 }}>
        {LEGEND.map((l) => (
          <span
            key={l.label}
            onMouseEnter={() => setHovered({ key: l.label as SentKey, index: null })}
            onMouseLeave={() => setHovered(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              cursor: 'pointer',
              borderRadius: 6,
              padding: '2px 6px',
              margin: '-2px -6px',
              background: hovered?.key === l.label && hovered.index == null ? 'var(--muted)' : 'transparent',
              transition: 'background-color 0.15s ease',
            }}
          >
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: l.color }} />
            <span style={{ fontSize: 14, fontWeight: 500, color: '#404040' }}>{l.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
